import { useMemo, useRef } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import type { IRealtimeEntity } from '@interfaces/realtimeEntities';

import { collectStandingRegions } from '../mapEditorUnreal';
import {
  readTriggerName,
  SCRIPTABLE_REGION_TYPES,
  TRIGGER_NAME_PROPERTY,
} from '../triggerRegions';

export { SCRIPTABLE_REGION_TYPES, TRIGGER_NAME_PROPERTY };

// Feature toggle: when true, a spot (ui_click) AI script/knowledge entry is
// only creatable/visible/selectable when the spot is owned by a scriptable UE
// trigger region (RegionIndex.spotToRegion) — i.e. "linked to a trigger", as
// opposed to a standalone media placement with no trigger volume behind it in
// the build. Flip to false to restore the old behavior where any spot can
// carry its own standalone ui_click script/knowledge. Shared by
// AiScriptsTreeSection.tsx (sidebar/create-flow) and EditorMode.tsx (WS-driven
// live-selection bridges), so both stay in sync.
export const REQUIRE_SPOT_TRIGGER_LINK = true;

export interface RegionIndex {
  // Scriptable region names for the current map (type MytaAITrigger or
  // MytaVideoTrigger — see toRegionList below) PLUS any dynamic trigger names
  // (see dynamicTriggerNames below) — the merged list callers should treat as
  // "known/scriptable triggers".
  regionNames: string[];
  // regionName -> the spot ueIdentifiers it owns (region.spots[]).
  regionSpots: Record<string, string[]>;
  // spotId (lowercased) -> owning region name. Lets a spot resolve to its
  // region so a script created for the region also covers the spot.
  spotToRegion: Record<string, string>;
  // Name of the region flagged isStartingPoint (the map's real "on join"
  // trigger), when the current map has one — undefined for maps that still
  // rely on the synthetic 'landing' pseudo-trigger.
  startingPointRegionName: string | undefined;
  // Subset of regionNames reported by dynamically-spawned level chunks (no
  // static region behind them) — lets consumers (e.g. AiScriptsTreeSection)
  // bucket them separately from build-shipped regions instead of treating
  // "New in build" and "runtime-discovered" as the same thing.
  dynamicTriggerNames: string[];
  // Subset of regionNames that are trigger regions the web spawned at runtime
  // and the author saved — the saved TriggerName of each. Bucketed on its own
  // in the tree, like dynamicTriggerNames.
  spawnedTriggerNames: string[];
  // Names that came from the build metadata or a standing layout room, i.e.
  // regions with a volume the web did not create. regionNames dedupes across
  // sources, so this is the only way to tell that a spawned name is also a
  // build region.
  buildRegionNames: string[];
  // The live TriggerName of every scriptable entity in the registry, saved or
  // not. Deliberately not merged into regionNames: the list is saved-only, but
  // the walk-in registration must still recognise a trigger this hub spawned
  // and has not saved yet.
  liveSpawnedTriggerNames: string[];
}

const EMPTY_INDEX: RegionIndex = {
  regionNames: [],
  regionSpots: {},
  spotToRegion: {},
  startingPointRegionName: undefined,
  dynamicTriggerNames: [],
  spawnedTriggerNames: [],
  buildRegionNames: [],
  liveSpawnedTriggerNames: [],
};

/**
 * Collects the trigger names of the registry's scriptable entities, deduped, in
 * registry order — reading each name off whichever property bag the caller
 * selects.
 *
 * @param entities - The session registry of spawned entities.
 * @param selectProperties - Picks the bag to read the name from; an entity it
 * returns nothing for is skipped.
 * @returns The names, empty when nothing scriptable is spawned.
 */
const collectTriggerNames = (
  entities: IRealtimeEntity[],
  selectProperties: (
    entity: IRealtimeEntity,
  ) => IRealtimeEntity['properties'] | undefined,
): string[] => {
  const names: string[] = [];
  for (const entity of entities) {
    if (!SCRIPTABLE_REGION_TYPES.has(entity.typeId)) {
      continue;
    }
    const name = readTriggerName(selectProperties(entity));
    if (name && !names.includes(name)) {
      names.push(name);
    }
  }
  return names;
};

/**
 * Collects the region names the saved spawned triggers stand under — the saved
 * TriggerName of every scriptable entity that has a save. Only a saved row can
 * carry a script: an unsaved spawn dies with the session, and reading the
 * saved name rather than the live one keeps this list equal to what every
 * other reader of the backend sees.
 *
 * @param entities - The session registry of spawned entities.
 * @returns The names, empty when nothing scriptable has been saved.
 */
export const collectSpawnedRegionNames = (
  entities: IRealtimeEntity[],
): string[] =>
  collectTriggerNames(entities, (entity) => entity.saved?.properties);

/**
 * Collects the live TriggerName of every scriptable entity in the registry,
 * saved or not.
 *
 * @param entities - The session registry of spawned entities.
 * @returns The names, empty when nothing scriptable is spawned.
 */
export const collectLiveSpawnedTriggerNames = (
  entities: IRealtimeEntity[],
): string[] => collectTriggerNames(entities, (entity) => entity.properties);

/**
 * Reads the scriptable regions — and, crucially, the `spots[]` array each
 * region owns — from the event's pixel-streaming client resources
 * (`clientParams.resources.maps[].regions`, the same source the admin uses),
 * falling back to the room's map metadata. This is the bridge between an AI
 * trigger ("Expo #1") and the spots that live inside it ("WallArt_Pic_01"), so
 * one script can be created for both at once.
 *
 * Union of four sources, because the build metadata cannot describe what the
 * world grows at runtime. A module's regions are authored into its level asset,
 * so placing the module twice put one name on two regions; UE now renames each
 * to `<roomKey>#<suffix>` per placement and reports the result in
 * CURRENT_GEOMETRY_LIST, and it is that renamed form which arrives as
 * `audioTrigger.aiTriggerId`, which a spot click reports, and which
 * TELEPORT_TO_TRIGGER resolves. Nothing about it can be derived here: the
 * metadata holds only the authored names and drops an unnamed region entirely.
 * Triggers reported by dynamically-spawned chunks arrive as registered dynamic
 * rows, and trigger regions the web itself spawned come from the session
 * registry — saved ones only, under their saved TriggerName.
 *
 * Service maps (`isServiceMap === true`) are dropped before anything is read:
 * their regions belong to levels no participant can be in, so they would only
 * add phantom triggers and spots to the sidebar.
 *
 * @param mapName - Current map, used to scope the resource maps; every map's
 * regions are read when it is unknown or matches none.
 * @returns The index; `EMPTY_INDEX` when no source has anything to say.
 */
export const useRegionIndex = (mapName: string | undefined): RegionIndex => {
  const {
    currentRoom,
    pixelStreamingClient,
    dynamicAiTriggers,
    currentGeometryRooms,
    currentGeometryStartRoom,
    realtimeEntities,
  } = useMytaverseEvent();

  const computed = useMemo<RegionIndex>(() => {
    type Region = {
      name?: unknown;
      type?: unknown;
      spots?: unknown;
      isStartingPoint?: unknown;
    };
    type ResourceMap = {
      name?: unknown;
      regions?: Region[];
      isServiceMap?: unknown;
    };

    // Service map: a level no participant can ever be in — a constructor prefab
    // room, a sublevel, a test level. The backend keeps them in the persisted
    // `resources` verbatim and filters at every read, so the hub filters here
    // too (it cannot import the backend util). Strictly `=== true`: UE only
    // starts sending the flag with MV-1826, and absent/`null`/`false` all mean a
    // content map, so nothing changes for the builds shipped so far.
    const isServiceMap = (map: ResourceMap | undefined): boolean =>
      map?.isServiceMap === true;

    const toRegionList = (regions: Region[] | undefined): Region[] =>
      Array.isArray(regions)
        ? regions.filter(
            (r) =>
              typeof r?.type === 'string' &&
              SCRIPTABLE_REGION_TYPES.has(r.type),
          )
        : [];

    const resourceMaps = (
      pixelStreamingClient as
        | { clientParams?: { resources?: { maps?: ResourceMap[] } } }
        | null
        | undefined
    )?.clientParams?.resources?.maps;

    // An entry has to be an object before any field of it can be read; the
    // backend util drops the rest for the same reason.
    const isMetadataObject = (entry: unknown): boolean =>
      typeof entry === 'object' && entry !== null && !Array.isArray(entry);

    const contentMaps = Array.isArray(resourceMaps)
      ? resourceMaps.filter((m) => isMetadataObject(m) && !isServiceMap(m))
      : [];

    let regions: Region[] = [];
    if (contentMaps.length > 0) {
      // The resource map name is a path like "/…/Maps/Map_SpiralMuseum"; prefer
      // the one matching the current map, otherwise flatten all maps.
      const scoped = mapName
        ? contentMaps.filter(
            (m) => typeof m?.name === 'string' && m.name.includes(mapName),
          )
        : [];
      const source = scoped.length > 0 ? scoped : contentMaps;
      regions = source.flatMap((m) => toRegionList(m?.regions));
    }

    if (regions.length === 0) {
      const roomRegions = (
        currentRoom?.map as { regions?: Region[] } | undefined
      )?.regions;
      regions = toRegionList(roomRegions);
    }

    // The lobby is read back in alongside the author's chain. It is dropped from that chain on
    // purpose — it is not a room the author picked — but it is still a placement standing in the
    // world, and a runtime placement is the only thing that can name a region inside it: build
    // metadata describes an element, not where a copy of it ended up. Left out, the one room every
    // visitor is guaranteed to stand in is the one room no AI trigger can be bound to.
    const standingRegions = collectStandingRegions([
      ...(currentGeometryRooms ?? []),
      ...(currentGeometryStartRoom ? [currentGeometryStartRoom] : []),
    ]);

    const spawnedEntities = Array.isArray(realtimeEntities)
      ? realtimeEntities
      : [];
    const spawnedTriggerNames = collectSpawnedRegionNames(spawnedEntities);
    const liveSpawnedTriggerNames =
      collectLiveSpawnedTriggerNames(spawnedEntities);

    if (
      regions.length === 0 &&
      standingRegions.length === 0 &&
      dynamicAiTriggers.length === 0 &&
      spawnedTriggerNames.length === 0
    ) {
      return { ...EMPTY_INDEX, liveSpawnedTriggerNames };
    }

    const regionNames: string[] = [];
    const regionSpots: Record<string, string[]> = {};
    const spotToRegion: Record<string, string> = {};
    let startingPointRegionName: string | undefined;

    for (const region of regions) {
      const name = typeof region.name === 'string' ? region.name : '';
      if (!name) {
        continue;
      }
      if (!regionNames.includes(name)) {
        regionNames.push(name);
      }
      if (region.isStartingPoint === true && !startingPointRegionName) {
        startingPointRegionName = name;
      }
      const spots = Array.isArray(region.spots)
        ? region.spots.filter((s): s is string => typeof s === 'string' && !!s)
        : [];
      regionSpots[name] = spots;
      // Self-map the region's own name too — a ui_click script can be bound
      // directly to the region/AI-trigger name itself rather than to a
      // nested spot ueIdentifier (e.g. a script authored against "Expo #1"
      // rather than a spot inside it). Without this, REQUIRE_SPOT_TRIGGER_LINK
      // (AiScriptsTreeSection) treats that script as an unlinked standalone
      // spot and drops it from the tree, even though useAiInspectorEntity
      // (unfiltered) resolves it fine — the exact mismatch that made the tree
      // show "No script" for a trigger the Inspector opened with real content.
      spotToRegion[name.toLowerCase()] = name;
      for (const spot of spots) {
        spotToRegion[spot.toLowerCase()] = name;
      }
    }

    // Map-editor placements: a module's regions are authored into its level
    // asset, so placing the module twice put one name on two regions; UE
    // renames each to `<roomKey>#<suffix>` per placement and reports the
    // result in CURRENT_GEOMETRY_LIST. Merge those renamed regions/spots in
    // alongside the build-metadata ones above.
    for (const { regionName, spotIds } of standingRegions) {
      if (!regionNames.includes(regionName)) {
        regionNames.push(regionName);
      }
      const known = regionSpots[regionName] ?? [];
      regionSpots[regionName] = known.concat(
        spotIds.filter((spot) => !known.includes(spot)),
      );
      // Self-map, same reasoning as the static-region loop above.
      spotToRegion[regionName.toLowerCase()] = regionName;
      for (const spot of spotIds) {
        spotToRegion[spot.toLowerCase()] = regionName;
      }
    }

    // Everything named so far has a volume the web did not create: the build's
    // regions and the standing layout rooms'. Snapshotted before the runtime
    // sources join, because regionNames dedupes across sources and could not
    // say afterwards whether a spawned name is also one of these.
    const buildRegionNames = [...regionNames];

    // Trigger regions the web spawned and the author saved. A runtime volume
    // owns no spots — the region-to-spot pairing exists only in the build-time
    // export — so the entry is the name alone, self-mapped like the others. A
    // name the build already declares is one region seen from two sides and
    // merges into it rather than replacing it.
    for (const name of spawnedTriggerNames) {
      if (!regionNames.includes(name)) {
        regionNames.push(name);
      }
      regionSpots[name] = regionSpots[name] ?? [];
      spotToRegion[name.toLowerCase()] = name;
    }

    // Runtime-discovered triggers (dynamically-spawned level chunks) have no
    // spots/isStartingPoint of their own — merge their names in as
    // synthetic, otherwise-empty entries so they're "known" the same way a
    // static region is, without pretending they carry static-region data.
    // Self-map into spotToRegion too: a dynamic trigger's own name doubles as
    // both the "region" and the "spot" a ui_click script binds to, so without
    // this, REQUIRE_SPOT_TRIGGER_LINK (AiScriptsTreeSection) treats a ui_click
    // script attached to it as an unlinked standalone spot and never counts
    // the trigger as covered. A name a saved spawned trigger already holds is
    // that trigger's own walk-in registration, so the spawned bucket keeps it —
    // compared the way scripts are matched to regions, without case.
    const spawnedTriggerNamesLower = new Set(
      spawnedTriggerNames.map((name) => name.toLowerCase()),
    );
    const dynamicTriggerNames: string[] = [];
    for (const trigger of dynamicAiTriggers) {
      if (spawnedTriggerNamesLower.has(trigger.triggerName.toLowerCase())) {
        continue;
      }
      if (!regionNames.includes(trigger.triggerName)) {
        regionNames.push(trigger.triggerName);
      }
      dynamicTriggerNames.push(trigger.triggerName);
      spotToRegion[trigger.triggerName.toLowerCase()] = trigger.triggerName;
    }

    return {
      regionNames,
      regionSpots,
      spotToRegion,
      startingPointRegionName,
      dynamicTriggerNames,
      spawnedTriggerNames,
      buildRegionNames,
      liveSpawnedTriggerNames,
    };
  }, [
    pixelStreamingClient,
    mapName,
    currentRoom?.map,
    currentGeometryRooms,
    currentGeometryStartRoom,
    dynamicAiTriggers,
    realtimeEntities,
  ]);

  // The registry changes identity on every transform blur and gizmo report, and
  // several effects are keyed on the index object itself (the UE selection
  // bridge re-runs a scene click on a new index). Hand back the previous object
  // while the content is the same, so an edit that names no region moves none
  // of them.
  const stableRef = useRef(computed);
  const signature = JSON.stringify(computed);
  if (JSON.stringify(stableRef.current) !== signature) {
    stableRef.current = computed;
  }
  return stableRef.current;
};
